import { map } from 'rxjs'
import { tap } from 'rxjs/operators'
import { action } from 'mobx'
import { Id } from '@/infra/id'
import { collection } from '@/infra/collection'
import type { JobsApi } from '@/features/jobs/api/job-api'
import { memoizeAsync } from '@/utils/memoize-decorator'
import { clearMemoized } from '@/utils/memoize-util'
import { GetTxfJobRequest } from '@taxfyle/api-internal/internal/txf_pb'
import type { TxfJobUpdatedRealtimeEvent } from '@taxfyle/api-internal/internal/txf_pb'
import { onRpcNotFoundReturnNull } from '@/utils/grpc-util'
import { filterNonNil } from '@/utils/rx-util'
import TxfJob from '@/features/jobs/details/tax-type/txf/TxfJob'
import { mapTxfJobProtoToProps } from '@/features/jobs/details/tax-type/txf/txf-mappers'

/**
 * Manages TXF integrations for a job.
 */
export class TxfJobStore {
  readonly txfJobs = collection({
    create: () => new TxfJob(),
    getModelId: (m) => m.jobId,
    getDataId: (d) => d.jobId,
  })

  constructor(private _api: JobsApi) {
    // Reload the TXF job when an update comes in.
    this._api.realtime$
      .pipe(
        map((e) => e.getTxfJobUpdated()),
        filterNonNil(),
        tap(this._onRemoteTxfJobUpdated),
      )
      .subscribe()
  }

  /**
   * Fetches the matching TXF job.
   */
  @memoizeAsync()
  async fetchTxfJob(jobId: Id, signal?: AbortSignal): Promise<TxfJob | null> {
    signal?.throwIfAborted()
    jobId = Id.assert(jobId)

    const request = new GetTxfJobRequest().setJobId(jobId)

    return this._api.txf
      .getTxfJob(request)
      .then((response) => {
        signal?.throwIfAborted()

        const props = mapTxfJobProtoToProps(jobId, response)
        return this.txfJobs.set(props)
      })
      .catch(onRpcNotFoundReturnNull)
  }

  /**
   * Whenever a TXF job has been updated, sync its contents.
   *
   * @param event
   * @private
   */
  @action.bound
  private _onRemoteTxfJobUpdated(event: TxfJobUpdatedRealtimeEvent) {
    const txfJobResource = event.getTxfJob()
    if (!txfJobResource) {
      return
    }

    const props = mapTxfJobProtoToProps(
      txfJobResource.getJobId(),
      txfJobResource,
    )

    const txfJob = this.txfJobs.get(props.jobId)
    if (!txfJob || props.modifiedAt > txfJob.modifiedAt) {
      this.txfJobs.set(props)
      clearMemoized(this.fetchTxfJob, props.jobId)
    }
  }
}
